import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, AlertTriangle, Clock, CheckCircle2, Upload, FileCheck, CircleDot } from 'lucide-react';
import { assessmentApi, remediationApi, queryKeys } from '../services/api';
import dayjs from 'dayjs';

const PRIORITY_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  CRITICAL: { label: 'Khẩn cấp', color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/30' },
  HIGH:     { label: 'Cao',       color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  MEDIUM:   { label: 'Trung bình',color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30' },
  LOW:      { label: 'Thấp',      color: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/30' },
};

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: JSX.Element; bg: string }> = {
  OPEN:        { label: 'Mở',          color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/10', icon: <CircleDot size={14}/> },
  IN_PROGRESS: { label: 'Đang xử lý',  color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10', icon: <Clock size={14}/> },
  RESOLVED:    { label: 'Đã khắc phục',color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/10', icon: <CheckCircle2 size={14}/> },
  OVERDUE:     { label: 'Quá hạn',     color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-500/10', icon: <AlertTriangle size={14}/> },
};

export default function RemediationPage() {
  const { id } = useParams<{ id: string }>();

  const { data: session } = useQuery({
    queryKey: queryKeys.sessions.detail(id!),
    queryFn:  () => assessmentApi.get(id!),
    enabled:  !!id,
  });

  const { data: remediations = [], isLoading } = useQuery({
    queryKey: queryKeys.remediations.bySession(id!),
    queryFn:  () => remediationApi.getBySession(id!),
    enabled:  !!id,
  });

  const openCount     = remediations.filter(r => r.status === 'OPEN').length;
  const overdueCount  = remediations.filter(r => r.status === 'OVERDUE').length;
  const resolvedCount = remediations.filter(r => r.status === 'RESOLVED').length;

  if (isLoading) return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-slate-400">
      <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-6" />
      <p className="text-[10px] font-black tracking-tight animate-pulse">Đang đồng bộ kiến nghị khắc phục...</p>
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div>
        <Link to={`/assessments/${id}`} className="inline-flex items-center gap-2 text-[10px] font-black text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-white mb-6 tracking-tight transition-all group">
          <div className="p-1.5 bg-indigo-500/10 rounded-lg group-hover:scale-110 transition-transform">
            <ArrowLeft size={14}/>
          </div>
          Quay lại hồ sơ {session?.sessionCode}
        </Link>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight leading-tight font-heading">
              Kiến nghị khắc phục
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-2 font-bold tracking-tight text-sm">Quản lý và thực thi các biện pháp xử lý rủi ro dữ liệu</p>
          </div>
          <div className="flex items-center gap-6 glass-panel px-8 py-4 rounded-[2rem] border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 flex items-center justify-center text-rose-600 dark:text-rose-400 shadow-inner">
                 <AlertTriangle size={18} />
              </div>
              <div>
                 <p className="text-xl font-black text-slate-900 dark:text-white tracking-tighter leading-none">{overdueCount}</p>
                 <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 tracking-tight mt-1">Quá hạn</p>
              </div>
            </div>
            <div className="w-px h-8 bg-slate-100 dark:bg-white/5" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400 shadow-inner">
                 <CircleDot size={18} />
              </div>
              <div>
                 <p className="text-xl font-black text-slate-900 dark:text-white tracking-tighter leading-none">{openCount}</p>
                 <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 tracking-tight mt-1">Đang mở</p>
              </div>
            </div>
            <div className="w-px h-8 bg-slate-100 dark:bg-white/5" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shadow-inner">
                 <CheckCircle2 size={18} />
              </div>
              <div>
                 <p className="text-xl font-black text-slate-900 dark:text-white tracking-tighter leading-none">{resolvedCount}</p>
                 <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 tracking-tight mt-1">Đã xử lý</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-5">
        {remediations.length === 0 && !isLoading ? (
          <div className="glass-panel text-center py-24 flex flex-col items-center border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.01] rounded-[2.5rem]">
            <div className="w-24 h-24 bg-emerald-50 dark:bg-emerald-500/10 rounded-full flex items-center justify-center mb-6 shadow-xl shadow-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20">
              <FileCheck size={44} className="text-emerald-500 dark:text-emerald-400" />
            </div>
            <h3 className="text-[11px] font-black text-slate-900 dark:text-white tracking-tight mb-2">Tuân thủ hoàn toàn</h3>
            <p className="text-slate-400 dark:text-slate-500 text-xs font-bold max-w-sm leading-relaxed">Đơn vị đã đáp ứng đầy đủ các tiêu chuẩn kỹ thuật.</p>
          </div>
        ) : (
          remediations.map(r => {
            const p = PRIORITY_CONFIG[r.priority as keyof typeof PRIORITY_CONFIG] || PRIORITY_CONFIG.LOW;
            const s = STATUS_CONFIG[r.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.OPEN;
            const isOverdue = r.status !== 'RESOLVED' && dayjs(r.deadline).isBefore(dayjs());

            return (
              <div 
                key={r.id} 
                className={`glass-panel border-l-[6px] border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.01] rounded-[2rem] overflow-hidden relative group transition-all transform hover:-translate-x-1 shadow-sm ${
                  isOverdue ? 'border-l-rose-500 dark:border-l-rose-500 shadow-rose-500/5' : 'border-l-indigo-600 dark:border-l-indigo-500'
                }`}
              >
                {isOverdue && (
                  <div className="absolute top-0 left-0 w-32 h-full bg-rose-500/[0.03] dark:bg-rose-500/5 blur-xl pointer-events-none" />
                )}
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 p-8 relative z-10">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="text-[10px] font-black font-mono text-indigo-600 dark:text-indigo-400 bg-white dark:bg-black/40 border border-slate-100 dark:border-white/10 px-3 py-1.5 rounded-xl shadow-sm tracking-tight">
                        {r.itemCode}
                      </span>
                      <span className={`text-[10px] px-3 py-1.5 rounded-xl border font-black tracking-tight shadow-sm ${p.bg} ${p.color} ${p.border}`}>
                        {p.label}
                      </span>
                      <span className={`flex items-center gap-2 text-[10px] font-black px-3 py-1.5 rounded-xl shadow-sm tracking-tight ${s.bg} ${s.color}`}>
                        {s.icon} {s.label}
                      </span>
                    </div>
                    <h3 className="text-[15px] font-black text-slate-900 dark:text-white mb-3 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors tracking-tight font-heading">
                      {r.title}
                    </h3>
                    <p className="text-[13px] font-bold text-slate-500 dark:text-slate-400 leading-relaxed max-w-4xl line-clamp-2">
                      {r.description}
                    </p>
                  </div>
                  
                  <div className="flex flex-col gap-4 shrink-0 w-full lg:w-max">
                     <div className="bg-slate-50 dark:bg-black/40 p-5 rounded-[1.5rem] border border-slate-100 dark:border-white/5 min-w-[200px] shadow-inner text-center">
                       <p className="text-[9px] text-slate-400 dark:text-slate-600 font-black tracking-tight mb-2">Hạn định khắc phục</p>
                       <p className={`text-2xl font-black tracking-tighter ${isOverdue ? 'text-rose-600 dark:text-rose-400 drop-shadow-sm dark:drop-shadow-[0_0_10px_currentColor]' : 'text-slate-800 dark:text-white'}`}>
                         {dayjs(r.deadline).format('DD/MM/YYYY')}
                       </p>
                       {isOverdue && <p className="text-[9px] text-rose-600 dark:text-rose-500 font-black mt-2 tracking-tight">Vi phạm tiến độ</p>}
                     </div>
                     
                     {r.status !== 'RESOLVED' && (
                       <button className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white text-[10px] font-black tracking-tight rounded-2xl shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 transition-all active:scale-95 transform hover:-translate-y-0.5">
                         <Upload size={14} /> Nộp bằng chứng
                       </button>
                     )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
